/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import {
  BottomNavigation, BottomNavigationAction, Paper,
} from '@mui/material';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import BrushIcon from '@mui/icons-material/Brush';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { jsPDF } from 'jspdf';
import { ColorPickerDialog } from './ColorPickerDialog';
import { useSignaturePadProvider } from '../../Provider/SignaturePadProvider';

/**
 * SignatureDrawPad Component
 * @returns {Element}
 * @constructor
 */
export function SignatureDrawControls() {
  // Provider values
  const {
    imagesData,
    signaturePadsReference,
    setColor,
    navigationAction,
    setNavigationAction,
    drawPadChanges,
  } = useSignaturePadProvider();

  // Color picker dialog state
  const [open, setOpen] = React.useState(false);

  /**
     * Sets the last used action based on the current navigation action.
     *
     * This function updates the navigation action to either 'paint' or 'mark'
     * based on the current value of `navigationAction`. If the current action
     * is not recognized, it defaults to 'paint'.
     */
  const setLastUsedAction = () => {
    switch (navigationAction) {
      case 'paint':
        setNavigationAction('paint');
        break;
      case 'mark':
        setNavigationAction('mark');
        break;
      default:
        setNavigationAction('paint');
    }
  };

  /**
     * Handles the opening of the color picker.
     *
     * This function sets the `open` state to true, thereby opening the color picker,
     * and calls the `setLastUsedAction` function
     */
  const handleColorPickerOpen = () => {
    setOpen(true);
    setLastUsedAction();
  };

  /**
     * Handles the closure of the color picker and updates the selected color.
     *
     * This function is called when the color picker is closed. It sets the `open` state to false,
     * updates the selected color with the provided `newValue`.
     *
     * @param {string} newValue - The new color value selected from the color picker.
     */
  const handleColorPickerClose = (newValue) => {
    setOpen(false);
    setColor(newValue);
  };

  /**
     * Downloads a PDF document containing images from signature pads.
     *
     * This function creates a PDF document using the jsPDF library. It iterates through the
     * `imagesData` array, adds each image to a new page in the PDF, and then saves the document
     * as 'filename.pdf'. After saving the PDF, it calls the `setLastUsedAction` function.
     */
  const downloadPdf = () => {
    // Create a new jsPDF document with default settings (A4 paper, portrait, mm units)
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();

    imagesData.forEach((imageData, index) => {
      if (index > 0) {
        doc.addPage();
      }
      const imageUrl = signaturePadsReference.current[index].toDataURL();
      doc.addImage(imageUrl, 'PNG', 0, 0, 210, 300);
    });

    doc.save('filename.pdf');
    setLastUsedAction();
  };

  /**
     * Clears all draw pads and restores their images.
     *
     * This function iterates through the `signaturePadsReference` array, clears each draw pad,
     * and restores the image from the `imagesData` array using the `fromDataURL` method. After
     * processing all draw pads, it calls the `setLastUsedAction` function.
     */
  const clearDrawPad = () => {
    signaturePadsReference.current.forEach((drawPadReference, index) => {
      drawPadReference.clear();
      drawPadReference.fromDataURL(imagesData[index].image, { ratio: 1 });
    });
    setLastUsedAction();
  };

  /**
     * Removes the last change made to a draw pad and restores the previous state.
     *
     * This function checks if there are any changes in the `drawPadChanges` array. If so,
     * it removes the last change and restores the corresponding draw pad to its previous state
     * using the `fromDataURL` method. After handling the change, it calls the `setLastUsedAction` function.
     */
  const handleRemoveLast = () => {
    if (drawPadChanges.length > 0) {
      const lastChange = drawPadChanges.pop();
      signaturePadsReference.current[lastChange.index].fromDataURL(lastChange.image, { ratio: 1 });
    }
    setLastUsedAction();
  };

  return (
    <Paper
      sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 105,
      }}
      elevation={5}
    >
      <BottomNavigation
        showLabels
        value={navigationAction}
        onChange={(event, newValue) => {
          setNavigationAction(newValue);
        }}
      >
        <BottomNavigationAction label="Touch" value="touch" icon={<TouchAppIcon />} />
        <BottomNavigationAction label="Paint" value="paint" icon={<BrushIcon />} />
        <BottomNavigationAction label="Mark" value="mark" icon={<FormatPaintIcon />} />
        <BottomNavigationAction
          label="Color"
          value="color"
          onClick={handleColorPickerOpen}
          icon={<ColorLensIcon />}
        />
        <BottomNavigationAction
          label="Undo"
          disabled={!drawPadChanges.length > 0}
          value="remove-last"
          onClick={handleRemoveLast}
          icon={<AutoFixHighIcon />}
        />
        <BottomNavigationAction label="Clear" value="clear" onClick={clearDrawPad} icon={<DeleteForeverIcon />} />
        <BottomNavigationAction label="Download" onClick={downloadPdf} value="download" icon={<DownloadIcon />} />
        <ColorPickerDialog open={open} onClose={handleColorPickerClose} />
      </BottomNavigation>
    </Paper>
  );
}
